import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { getApiBaseUrl, getAuthHeaders } from "../utils/apiHelpers";
import { TIP_PRESET_PERCENTAGES, DEFAULT_TIP_PERCENTAGE } from "../constants/tips";
// F2 options UI: the SAME selector and the SAME display mirror the customer checkout
// and the cart widget use, so the desk sees the numbers the server will charge.
import { ServicePricingSelector } from "./BookingFlowPanels";
import { resolveServiceLines } from "../utils/servicePricing";
import { resolveProductImageUrl } from "../utils/imageUrlHelpers";
import "./WalkInPaymentModal.css";

/**
 * WalkInPaymentModal — shared walk-in payment UI for ALL FIVE roles
 * (R1, R2, R3, R4, R7).
 *
 * Phase 1 backend contract:
 *   POST /api/bookings/walk-in                 -> create (status: pending_payment)
 *   POST /api/bookings/walk-in/:id/cash        -> confirm a cash walk-in
 *   GET  /api/bookings/:id                     -> poll payment status
 *
 * Notes that drove this design:
 *  - The booking is created as `pending_payment` and only becomes paid when the
 *    money lands. For card (qr/link/reader) the Stripe webhook confirms it, so we
 *    POLL the booking rather than assuming success.
 *  - A clash with another appointment comes back as HTTP 409 `overlap_warning`.
 *    It is a warning, not a failure: the staff member resends with
 *    `acknowledgeOverlap: true`. Never silently swallowed.
 *  - R1/R2 must name the delivering professional (`requireProvider`); R3/R4/R7
 *    default to themselves, so the selector is hidden for them.
 *  - CASH + products is rejected by the backend in v1 (products_require_card).
 */
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000; // matches the 30-min Stripe session

// Group members. A member is identified by a short LABEL rather than a full name, so
// the desk picks a relationship or types a name - the same idea, and the same options,
// as the customer booking page (CustomerBooking.js). Two additional members, matching
// the online limit.
const GROUP_RELATIONSHIP_OPTIONS = [
  "Son",
  "Daughter",
  "Spouse",
  "Partner",
  "Parent",
  "Sibling",
  "Friend",
];
const MAX_ADDITIONAL_GROUP_MEMBERS = 2;

/**
 * The in-flight walk-in, kept across a page reload.
 *
 * Creating a walk-in writes a real booking that HOLDS a real slot, and the desk then
 * waits on the customer. A refresh - a mis-tap, a browser crash, a second tab - used to
 * destroy the only screen that could see the payment land. The booking was still fine,
 * the money still arrived, and nobody at the desk knew: the slot countdown, the QR and
 * the live status were all gone, and reopening `+ Walk-in` started a NEW walk-in instead
 * of reattaching to the one being paid for.
 *
 * `sessionStorage`, not `localStorage`: this describes what the CURRENT desk window was
 * doing, and it should not follow the user into another tab or survive the day. The
 * entry is validated against the server on resume, so a stale one (already paid, already
 * cancelled) resolves within one poll rather than lying.
 */
const PENDING_WALKIN_KEY = "ogestyle_walkin_pending";

function readPendingWalkIn() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_WALKIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.bookingId) return null;
    // Sanity bound: a walk-in is a same-visit problem, so anything older than a shift is
    // noise. Without this a forgotten entry would offer to "resume" yesterday's till.
    if (!parsed.savedAt || Date.now() - parsed.savedAt > 12 * 60 * 60 * 1000) {
      window.sessionStorage.removeItem(PENDING_WALKIN_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePendingWalkIn(entry) {
  try {
    window.sessionStorage.setItem(
      PENDING_WALKIN_KEY,
      JSON.stringify({ ...entry, savedAt: Date.now() }),
    );
  } catch {
    // Private mode / quota: the walk-in still works, it just cannot be resumed.
  }
}

function clearPendingWalkIn() {
  try {
    window.sessionStorage.removeItem(PENDING_WALKIN_KEY);
  } catch {
    // Nothing to do.
  }
}

/** "10:00 AM" from a 24-hour "HH:MM". Empty string for anything unusable. */
function formatClockTime(value) {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${
    hours >= 12 ? "PM" : "AM"
  }`;
}

/** "Saturday, September 26 at 10:00 AM" — for the offered replacement time. */
function formatProposalLabel(ymd, startTime, endTime) {
  let day = String(ymd || "");
  try {
    const date = new Date(`${ymd}T12:00:00`);
    if (!Number.isNaN(date.getTime())) {
      day = new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      }).format(date);
    }
  } catch {
    // Keep the raw date rather than showing nothing.
  }

  const start = formatClockTime(startTime);
  const end = formatClockTime(endTime);
  if (!start) return day;
  return `${day} at ${start}${end ? ` – ${end}` : ""}`;
}

/**
 * Today's date (YYYY-MM-DD) in a given timezone.
 *
 * `new Date().toISOString().slice(0, 10)` is the UTC date, and for every US
 * timezone UTC rolls over to TOMORROW in the evening — so an evening walk-in asked
 * the API for the next day's slots, and created the booking on the next day.
 * A walk-in is always today, at the business.
 *
 * `formatToParts` is used rather than `en-CA` formatting because the parts are
 * unambiguous; date-format output depends on the ICU build.
 */
function todayInTimezone(timezone) {
  const zone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const value = (type) =>
      parts.find((part) => part.type === type)?.value || "";
    const year = value("year");
    const month = value("month");
    const day = value("day");
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    // Unknown timezone string — fall through to the device clock.
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function WalkInPaymentModal({
  isOpen,
  onClose,
  onCompleted,
  services = [],
  requireProvider = false,
  // Location the walk-in belongs to, from the page. Used as the default; an R1 with
  // more than one location can change it with the picker below.
  shopId = null,
  // Every location the caller may use. Only the front desk is given a picker, and
  // only R1 can actually choose - R2 is pinned to a single location by design.
  locations = [],
  canChooseLocation = false,
  // The caller's OWN role, for R3/R4/R7 who deliver the walk-in themselves. The group
  // controls depend on the DELIVERING professional being an R3, and for a self-booking
  // that is simply the caller.
  providerSelfRole = "",
  // Preset provider id: for R3/R4/R7 that is themselves; for R1/R2 it is null and
  // the provider is chosen in the form below. Named differently from the `staffId`
  // state (the provider a R1/R2 picks) to avoid a name collision.
  presetStaffId = null,
  // The professional's/shop's timezone, from the staff or profile API. Used to work
  // out what "today" is for the slot picker — see todayInTimezone above.
  defaultTimezone = null,
  // 'card' shows the QR/link flow, 'cash' jumps straight to cash. Staff can
  // still switch inside the modal.
  defaultChannel = "qr",
  // A walk-in the desk asked to REOPEN from the bookings list, rather than a new one.
  // Sending the link does not mean the customer pays - phones die, cards get declined,
  // someone steps outside and comes back - so the desk needs a way back into a walk-in
  // that is still holding a slot, instead of waiting on a screen they already closed or
  // releasing the slot and starting again.
  reopenBookingId = null,
}) {
  const [step, setStep] = useState("form"); // form | pay | done
  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState(defaultChannel);
  const [cashAmount, setCashAmount] = useState("");
  const [created, setCreated] = useState(null); // { bookingId, stripeCheckoutUrl, totalAmount }
  // An in-flight walk-in recovered from sessionStorage after a reload. Offered, never
  // forced: reopening the modal mid-shift must still default to taking a NEW walk-in.
  const [resumable, setResumable] = useState(null);
  // What the server says about a REOPENED walk-in: whether its Stripe session is still
  // live, already paid, or dead. Drives whether a QR is shown at all.
  const [reopenInfo, setReopenInfo] = useState(null);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null);
  // Non-clash unavailability (almost/at closing time, a blocked window, a day off).
  // A warning the desk acknowledges, never a hard block — see the create route.
  const [availabilityWarning, setAvailabilityWarning] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [linkNotice, setLinkNotice] = useState("");
  // Set when a paid walk-in's slot was taken and another day is being offered. The
  // desk and the customer's own screen both show this question; the first answer wins.
  const [proposal, setProposal] = useState(null);
  // Paid, and the time the desk set is NOT the time it ended up at (the original slot was
  // taken while the customer was paying, so it was moved, or the desk placed it
  // somewhere else). Silent confirmation would leave the customer booked at a time the
  // desk never agreed to.
  const [movedNotice, setMovedNotice] = useState(null);
  // Paid, slot taken, and NOTHING was free today: the desk sets the time (Part 4).
  const [needsPlacement, setNeedsPlacement] = useState(false);
  // The time the desk typed for a parked walk-in, and the overlap warning it produced.
  const [placementTime, setPlacementTime] = useState("");
  const [placementConflict, setPlacementConflict] = useState(null);
  const proposalPendingRef = useRef(false);
  // Slot picker
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsNote, setSlotsNote] = useState("");
  const [startTime, setStartTime] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  // Bumped when the server tells us our date was wrong, so the slot fetch re-runs
  // with the corrected day.
  const [slotsRefreshKey, setSlotsRefreshKey] = useState(0);
  // F2 options: per-unit quantity, hourly length or the chosen price tier for the
  // primary service, plus any add-ons (each with its OWN selection, keyed by service
  // id, so one line can never mutate another).
  const [selection, setSelection] = useState({});
  const [addOnIds, setAddOnIds] = useState([]);
  const [addOnSelections, setAddOnSelections] = useState({});
  // L3: creating a walk-in writes a real booking that HOLDS a real slot for a few
  // minutes, so it has to be a deliberate act - not a side effect of pressing Continue.
  const [intentAcknowledged, setIntentAcknowledged] = useState(false);
  // Tips (Phase 1): chosen at the counter. Same percentages as the customer pages.
  const [tipPercentage, setTipPercentage] = useState(DEFAULT_TIP_PERCENTAGE);
  const [tipIsCustom, setTipIsCustom] = useState(false);
  const [customTip, setCustomTip] = useState("");
  // Products sold at the desk, keyed by ShopProduct id -> quantity. The API expects
  // `{ productId, quantity }` where productId is a ShopProduct (a VARIANT, when the
  // product has variants), so each pickable row is one variant.
  const [productOptions, setProductOptions] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  // Front desk only: the services belonging to the CHOSEN professional. The `services`
  // prop is the caller's own list, which is empty for an owner or admin.
  const [providerServices, setProviderServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  // Front desk only: the professionals at the chosen location, and which location that
  // is. An owner with several locations can write a walk-in for a site other than the
  // one their device is signed into, so the location is state, not just a prop.
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [locationId, setLocationId] = useState(shopId || "");
  // Group walk-in (R3 only). Every member becomes their OWN booking document in the
  // card webhook - their own calendar entry, availability block and payout ledger
  // entry - with the times chained one after another. The desk only names them and
  // picks each one's service; the server works out when each starts.
  const [groupMemberList, setGroupMemberList] = useState([]);

  const pollStartRef = useRef(0);
  const pollTimerRef = useRef(null);
  // Bumped whenever a poll is started or stopped, so a response that arrives after the modal moved on is inert.
  const pollGenerationRef = useRef(0);
  // The start time this walk-in was CREATED for.
  //
  // Deliberately a ref, not `created.startTime`: `startPolling` is a useCallback built
  // before `setCreated` has rendered, so reading `created` inside its interval would
  // capture null and the moved-time notice would silently never appear.
  const createdStartRef = useRef(null);

  const reset = useCallback(() => {
    setStep("form");
    setServiceId("");
    setStaffId("");
    setFirstName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setChannel(defaultChannel);
    setCashAmount("");
    setCreated(null);
    setOverlapWarning(null);
    setAvailabilityWarning(null);
    setError("");
    setBusy(false);
    setCopied(false);
    setConfirmRelease(false);
    setLinkNotice("");
    setProposal(null);
    setNeedsPlacement(false);
    setMovedNotice(null);
    createdStartRef.current = null;
    setReopenInfo(null);
    setPlacementTime("");;
    setPlacementConflict(null);
    proposalPendingRef.current = false;
    setSelection({});
    setAddOnIds([]);
    setAddOnSelections({});
    setIntentAcknowledged(false);
    // Not 0: the desk starts from the same default percentage as the customer pages, so a
    // walk-in cannot slip through with a silent $0 tip. Custom is how you leave nothing.
    setTipPercentage(DEFAULT_TIP_PERCENTAGE);
    setTipIsCustom(false);
    setCustomTip("");
    setProductQuantities({});
    setGroupMemberList([]);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [defaultChannel]);

  /**
   * EVERY exit closes the modal AND refreshes the page.
   *
   * The modal is opened FROM the bookings list, and `onClose` alone only hides the
   * modal — so a released, paid or still-held walk-in looked unchanged behind it.
   * Reloading is the only way the list provably matches the server after a walk-in
   * changed state, which is what staff check before taking the next customer.
   */
  const closeAndRefresh = useCallback(() => {
    reset();
    if (typeof onClose === "function") onClose();
    window.location.reload();
  }, [onClose, reset]);

  /**
   * Release the held slot. The endpoint expires the Stripe session BEFORE deleting
   * the booking, so the QR stops working and a late card payment cannot be orphaned
   * against a booking that no longer exists.
   */
  const releaseWalkIn = useCallback(async () => {
    setError("");
    if (!created?.bookingId) {
      setError("This walk-in has no booking id, so the slot cannot be released.");
      return;
    }
    setBusy(true);
    try {
      const base = getApiBaseUrl();
      // Authenticated release — the SAME route the bookings list uses.
      //
      // This previously called the public /cancel-pending-checkout route with
      // `checkoutCancelToken`, but the create response only returned that token
      // on the CASH branch, so a card walk-in reported "no cancel token" for a
      // booking this modal had just created. Authorisation here is the caller's
      // session rather than a token, so it works for every channel.
      await axios.post(
        `${base}/api/bookings/walk-in/${created.bookingId}/cancel`,
        {},
        { headers: getAuthHeaders() },
      );
      // Released on purpose: nothing is waiting on this walk-in any more.
      clearPendingWalkIn();
      closeAndRefresh();
    } catch (err) {
      // Previously swallowed, which made a failed release look like a no-op.
      const payload = err?.response?.data || {};
      setError(payload.message || "Unable to release this slot.");
    } finally {
      setBusy(false);
    }
  }, [closeAndRefresh, created]);

  /** Answer the offered replacement time. Same endpoint the customer's page uses. */
  const respondToProposal = useCallback(
    async (accept) => {
      if (!created?.bookingId) return;
      setBusy(true);
      setError("");
      try {
        const base = getApiBaseUrl();
        const res = await axios.post(
          `${base}/api/bookings/walk-in/${created.bookingId}/proposal`,
          { accept },
          { headers: getAuthHeaders() },
        );

        if (accept) {
          proposalPendingRef.current = false;
          setProposal(null);
          clearPendingWalkIn();
          setLinkNotice(res.data?.message || "Walk-in moved to the new time.");
          setStep("done");
          if (typeof onCompleted === "function") onCompleted(res.data?.booking);
          return;
        }

        // Declined: the payment is refunded, so leave the list in a clean state.
        clearPendingWalkIn();
        closeAndRefresh();
      } catch (err) {
        const payload = err?.response?.data || {};
        setError(payload.message || "Unable to record the decision.");
      } finally {
        setBusy(false);
      }
    },
    [closeAndRefresh, created, onCompleted],
  );

  /**
   * Place a PAID walk-in at a time the desk chose (Part 4).
   *
   * This is the recovery for "the slot went while they were paying, and nothing else was
   * free today". There is no slot to pick from - that is why it parked - so the desk
   * states the time and the professional has agreed to take the customer.
   *
   * A 409 `overlap` means the chosen time collides with another appointment. That is not
   * an error to swallow: the money is already taken, so the desk must see it and confirm
   * deliberately. The retry carries `acknowledgeOverlap`.
   */
  const placeWalkIn = useCallback(
    async ({ acknowledgeOverlap = false } = {}) => {
      if (!created?.bookingId || !placementTime) return;
      setBusy(true);
      setError("");
      try {
        const base = getApiBaseUrl();
        const res = await axios.post(
          `${base}/api/bookings/walk-in/${created.bookingId}/place`,
          { startTime: placementTime, acknowledgeOverlap },
          { headers: getAuthHeaders() },
        );

        proposalPendingRef.current = false;
        setNeedsPlacement(false);
        setPlacementConflict(null);
        setPlacementTime("");
        setLinkNotice(
          `Walk-in placed at ${res.data?.startTime || placementTime}.`,
        );
        setStep("done");
        if (typeof onCompleted === "function") onCompleted(res.data?.booking);
      } catch (err) {
        const payload = err?.response?.data || {};
        if (err?.response?.status === 409 && payload.error === "overlap") {
          // Ask for the deliberate confirmation rather than discarding the choice.
          setPlacementConflict(payload.message || "That time overlaps another appointment.");
        } else {
          setError(payload.message || "Unable to place this walk-in.");
        }
      } finally {
        setBusy(false);
      }
    },
    [created, onCompleted, placementTime],
  );

  /**
   * Email/text the payment link through OUR backend (Mailgun + Telnyx).
   *
   * These were `mailto:` and `sms:` links, which hand the Stripe URL to the
   * device's own mail or messaging app — on a shared desk tablet that is both
   * unreliable and a data leak. Now the send happens server-side and the modal
   * reports the real outcome.
   */
  const sendPaymentLink = useCallback(
    async (kind) => {
      if (!created?.bookingId) return;
      setBusy(true);
      setError("");
      setLinkNotice("");
      try {
        const base = getApiBaseUrl();
        const res = await axios.post(
          `${base}/api/bookings/walk-in/${created.bookingId}/send-payment-link`,
          {
            channel: kind,
            ...(kind === "email"
              ? { email: email.trim() }
              : { phone: phone.trim() }),
          },
          { headers: getAuthHeaders() },
        );
        setLinkNotice(res.data?.message || "Payment link sent.");
      } catch (err) {
        const payload = err?.response?.data || {};
        setError(payload.message || "Unable to send the payment link.");
      } finally {
        setBusy(false);
      }
    },
    [created, email, phone],
  );

  // L2: closing while a slot is still held must be a deliberate choice, not a
  // silent side effect. Previously Close just left the hold in place.
  //
  // UPDATED 2026-09-27: closing NO LONGER releases the slot, and no longer asks.
  // A walk-in waiting on a customer is a normal, resumable state now - it shows in the
  // bookings list as "Awaiting payment" and the modal reoffers it on reopen - so
  // intercepting Close with a release prompt made the desk think leaving was
  // destructive when it is not, and left them stuck on a screen they had no reason to
  // watch. Release moved to its own explicit button with its own confirmation.
  const handleClose = useCallback(() => {
    closeAndRefresh();
  }, [closeAndRefresh]);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  // On open, look for a walk-in this window was waiting on when it was interrupted.
  useEffect(() => {
    if (!isOpen) return;
    setResumable(readPendingWalkIn());
  }, [isOpen]);

  // Stop polling when the modal goes away, so a closed modal can never keep
  // hammering the API.
  useEffect(
    () => () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    },
    [],
  );

  // Front desk books on behalf of a professional, so the bookable list is theirs. Every
  // other role is handed their own list by the page.
  const serviceList = requireProvider ? providerServices : services;

  const selectedService = useMemo(
    () => serviceList.find((s) => String(s._id) === String(serviceId)) || null,
    [serviceList, serviceId],
  );

  // The professional who will deliver it — picked (R1/R2) or themselves (R3/R4/R7).
  const effectiveStaffId = staffId || presetStaffId;

  /**
   * F2 options resolution.
   *
   * Declared BEFORE the slot picker because the picker needs the resolved appointment
   * LENGTH to ask for the right slots. `resolveServiceLines` is the display mirror of
   * the server's authoritative engine, so the length quoted to the desk is the length
   * the booking is created at. The primary keeps the top-level selection; every add-on
   * carries its own.
   */
  const bundleServices = useMemo(() => {
    if (!selectedService) return [];
    const addOns = addOnIds
      .map((id) => serviceList.find((s) => String(s._id) === String(id)))
      .filter(Boolean);
    return [selectedService, ...addOns];
  }, [addOnIds, selectedService, serviceList]);

  const resolvedBundle = useMemo(
    () =>
      resolveServiceLines(
        bundleServices.map((svc) => ({
          service: svc,
          selection:
            String(svc._id) === String(selectedService?._id)
              ? selection
              : addOnSelections[String(svc._id)] || {},
        })),
      ),
    [addOnSelections, bundleServices, selectedService, selection],
  );

  // A line with no choice yet has no price and NO LENGTH. Falling back to the catalog
  // duration would be wrong for a range service, where the stored duration is the
  // LONGEST option - it would offer slots that cannot actually be delivered.
  const pendingSelectionLines = resolvedBundle.lines.filter(
    (line) => line?.requiresSelection,
  );
  const selectionPending = pendingSelectionLines.length > 0;
  const effectiveDuration = selectionPending
    ? 0
    : Number(resolvedBundle.totalMinutes) ||
      Number(selectedService?.durationMinutes) ||
      0;
  const effectivePrice = selectionPending ? 0 : Number(resolvedBundle.totalPrice) || 0;

  /**
   * Group walk-in support (R3 only).
   *
   * The rule is about the DELIVERING professional, not about who is at the desk:
   * an R1/R2 may take a group walk-in on an R3's behalf, but never for an R4 or R7,
   * because those roles have no group booking at all. So the controls stay hidden
   * until that professional is known.
   */
  const selectedProvider = requireProvider
    ? providers.find((p) => String(p._id) === String(effectiveStaffId)) || null
    : null;
  const providerRole = requireProvider
    ? selectedProvider?.role || ""
    : providerSelfRole;
  const canAddGroupMembers = providerRole === "R3";

  // Each member's own resolved line: their own price and LENGTH, keyed by that
  // member's own selection. Reusing the engine mirror keeps this identical to what
  // the server resolves, instead of re-deriving a price by hand.
  const groupMemberResolved = useMemo(
    () =>
      resolveServiceLines(
        groupMemberList
          .map((member) => {
            const svc = serviceList.find(
              (s) => String(s._id) === String(member.serviceId),
            );
            if (!svc) return null;
            return { service: svc, selection: member.selection || {} };
          })
          .filter(Boolean),
      ),
    [groupMemberList, serviceList],
  );
  const groupMembersTotal = groupMemberResolved.totalPrice;
  const groupMembersMinutes = groupMemberResolved.totalMinutes;
  // A member whose service needs an option chosen has no price and no length yet.
  const groupSelectionPending = groupMemberResolved.lines.some(
    (line) => line?.requiresSelection,
  );

  // The slot that has to be free is the WHOLE chain, not just the primary customer:
  // the members are served back-to-back after them, so offering a slot with only
  // room for the primary would book a chain that overruns the professional's day.
  const slotQueryDuration = effectiveDuration
    ? effectiveDuration + groupMembersMinutes
    : 0;

  // The group must be paid by card so the webhook can create each member's own
  // booking; a cash group would leave their services unscheduled and unpaid.
  const groupNeedsCard = channel === "cash" && groupMemberList.length > 0;

  // Members belong to the professional chosen to deliver them. Switching to one who
  // cannot take a group (R4/R7, or nobody selected yet) must DROP them rather than
  // leave them in state but off screen, where a later submit could still use them.
  useEffect(() => {
    if (!canAddGroupMembers) {
      setGroupMemberList((prev) => (prev.length ? [] : prev));
    }
  }, [canAddGroupMembers]);

  // A percentage tip is priced on the SERVICE value only — the server recomputes it the
  // same way, and a custom amount is sent as-is. Never on products or travel.
  //
  // For a group it covers EVERY service in the visit, not just the customer in front of
  // the desk: the same professional delivers all of them, so 12% on a parent + child is
  // 12% of both. The server uses the identical basis, so the figure shown here is the
  // figure charged.
  const tipAmount = useMemo(() => {
    if (tipIsCustom) {
      const value = Number(customTip);
      return Number.isFinite(value) && value > 0
        ? Math.round(value * 100) / 100
        : 0;
    }
    if (!tipPercentage) return 0;
    const tipBasis = effectivePrice + groupMembersTotal;
    return Math.round(((tipBasis * tipPercentage) / 100) * 100) / 100;
  }, [
    customTip,
    effectivePrice,
    groupMembersTotal,
    tipIsCustom,
    tipPercentage,
  ]);

  // A different professional delivers different services, so a choice made for the
  // previous one may not even be theirs. Reset it when the professional changes.
  useEffect(() => {
    setServiceId("");
  }, [effectiveStaffId]);

  // A selection belongs to the service and professional it was made for. Changing
  // either replaces what is being booked, so carrying the old choice over would price
  // (and schedule) the wrong thing.
  useEffect(() => {
    setSelection({});
    setAddOnIds([]);
    setAddOnSelections({});
  }, [serviceId, effectiveStaffId]);

  const toggleAddOn = useCallback((id) => {
    setAddOnIds((prev) =>
      prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id],
    );
    setAddOnSelections((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // Group walk-in members (R3 only). Every member is delivered by the SAME
  // professional, chained straight after the previous one, so the desk supplies only
  // a name and a service - the server works out each one's start time.
  const addGroupMember = useCallback(() => {
    setGroupMemberList((prev) => {
      // Cap it here as well as hiding the button: the limit is a product rule, not
      // just a UI preference.
      if (prev.length >= MAX_ADDITIONAL_GROUP_MEMBERS) return prev;
      return [...prev, { name: "", phone: "", serviceId: "", selection: {} }];
    });
  }, []);

  const updateGroupMember = useCallback((index, patch) => {
    setGroupMemberList((prev) =>
      prev.map((member, i) => (i === index ? { ...member, ...patch } : member)),
    );
  }, []);

  const removeGroupMember = useCallback((index) => {
    setGroupMemberList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Which location this walk-in belongs to.
   *
   * An R1 who owns several locations may be standing at one and serving a customer
   * who belongs to another, so the modal - not the page - decides. The page's location
   * is preferred when the caller is allowed to use it, otherwise the first one they
   * are allowed to use. Every other role keeps the page's location untouched.
   */
  useEffect(() => {
    if (!isOpen || !requireProvider) return;
    // `locations` may be a freshly-built array on every parent render, so only write
    // when the value actually changes - otherwise this would churn state each render.
    const allowed = (locations || []).map((loc) => String(loc._id));
    const preferred =
      shopId && (allowed.length === 0 || allowed.includes(String(shopId)))
        ? String(shopId)
        : locations?.length
          ? String(locations[0]._id)
          : "";
    if (!preferred) return;
    setLocationId((prev) => (prev === preferred ? prev : preferred));
  }, [isOpen, requireProvider, shopId, locations]);

  /**
   * Front desk: the professionals who can deliver at the CHOSEN location.
   *
   * `includeApprovedLocations=true` matches staff who are approved to work here, not
   * only those currently stationed here - otherwise an owner could never write a
   * walk-in for a site the professional is not signed in at. The create route enforces
   * the same rule, so this list and that check agree.
   */
  useEffect(() => {
    if (!isOpen || !requireProvider || !locationId) {
      setProviders([]);
      setProvidersLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProvidersLoading(true);
    (async () => {
      try {
        const base = getApiBaseUrl();
        const res = await axios.get(`${base}/api/shops/${locationId}/staff`, {
          headers: getAuthHeaders(),
          params: { includeApprovedLocations: true },
        });
        const list = res.data?.staff || res.data?.staffMembers || [];
        if (!cancelled) setProviders(list);
      } catch (err) {
        if (!cancelled) setProviders([]);
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, requireProvider, locationId]);

  // A professional belongs to one location's list. Changing the location must not
  // leave a professional selected who may not work there.
  useEffect(() => {
    setStaffId("");
  }, [locationId]);

  /**
   * Front desk: load the CHOSEN professional's services.
   *
   * `/api/staff/services` answers "my services", which is empty for an owner or admin -
   * that is exactly why both dropdowns were blank for R1/R2. The public shop-services
   * endpoint takes a staffId and returns the same pricing shape, so the options UI keeps
   * working unchanged.
   */
  useEffect(() => {
    if (!isOpen || !requireProvider || !locationId || !staffId) {
      setProviderServices([]);
      setServicesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setServicesLoading(true);
    (async () => {
      try {
        const base = getApiBaseUrl();
        const res = await axios.get(
          `${base}/api/staff/shop/${locationId}/services`,
          { headers: getAuthHeaders(), params: { staffId } },
        );
        if (!cancelled) setProviderServices(res.data?.services || []);
      } catch (err) {
        if (!cancelled) setProviderServices([]);
      } finally {
        if (!cancelled) setServicesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, requireProvider, locationId, staffId]);

  /**
   * Products the desk can sell with this walk-in.
   *
   * Reuses the booking-scoped recommended-products endpoint, which already filters to
   * in-stock and handles ownership: an R7 renter or R4 freelancer sees only THEIR
   * products, while a shop booking sees the location's range (excluding the
   * independents'). Each returned family is flattened to its VARIANTS, because a
   * variant - not a family - is what the booking API takes as `productId`.
   */
  useEffect(() => {
    if (!isOpen || !serviceId) {
      setProductOptions([]);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const base = getApiBaseUrl();
        const res = await axios.get(
          `${base}/api/bookings/recommended-products/${serviceId}`,
          { headers: getAuthHeaders() },
        );
        if (cancelled) return;

        const rows = [];
        for (const product of res.data?.products || []) {
          const familyImages = Array.isArray(product.images) ? product.images : [];
          for (const variant of product.variants || []) {
            const stock = Number(variant.stock) || 0;
            if (stock <= 0) continue;
            const variantImages = Array.isArray(variant.variantImages)
              ? variant.variantImages
              : [];
            rows.push({
              productId: String(variant._id),
              name: product.name || "Product",
              variantLabel:
                (product.variants || []).length > 1 ||
                variant.variantLabel !== "Default"
                  ? variant.variantLabel
                  : null,
              price: Number(variant.price) || 0,
              stock,
              // A variant's own photo wins; otherwise the family's. Resolved to an
              // absolute URL, because stored paths are relative to the API host.
              imageUrl: resolveProductImageUrl(
                { images: variantImages.length ? variantImages : familyImages },
                base,
              ),
            });
          }
        }
        setProductOptions(rows);
      } catch (err) {
        // A product list that will not load must not stop a walk-in being recorded.
        if (!cancelled) setProductOptions([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, serviceId]);

  const setProductQuantity = useCallback((productId, quantity) => {
    setProductQuantities((prev) => {
      const next = { ...prev };
      const value = Math.max(0, Math.floor(Number(quantity) || 0));
      if (value <= 0) {
        delete next[productId];
      } else {
        next[productId] = value;
      }
      return next;
    });
  }, []);

  const selectedProducts = useMemo(
    () =>
      Object.entries(productQuantities)
        .map(([productId, quantity]) => {
          const option = productOptions.find((p) => p.productId === productId);
          return option ? { ...option, quantity } : null;
        })
        .filter(Boolean),
    [productOptions, productQuantities],
  );

  const productsTotal = useMemo(
    () =>
      selectedProducts.reduce((sum, line) => sum + line.price * line.quantity, 0),
    [selectedProducts],
  );

  // v1 limitation, enforced by the cash route (`products_require_card`): a cash
  // walk-in cannot carry products, because the seller-payout path for them lives only
  // in the card webhook. Caught here so the desk is told BEFORE anything is created.
  const productsNeedCard = channel === "cash" && selectedProducts.length > 0;

  // Slot picker: fetch the professional's REAL availability so staff choose a
  // round, bookable time. Previously the modal sent no start time and the server
  // defaulted to "now" — which is how the diary got 4:11 PM and 4:52 PM entries.
  //
  // ALWAYS today, in the professional's timezone. A walk-in cannot be booked for
  // another day, so there is deliberately no date picker here.
  useEffect(() => {
    const effectiveStaff = staffId || presetStaffId;
    if (!isOpen || !serviceId || !effectiveStaff) {
      setSlots([]);
      setSlotsNote("");
      return undefined;
    }

    // No length to quote until the option is chosen, so do not ask for slots yet -
    // otherwise the list would be built for the wrong appointment length.
    if (!slotQueryDuration) {
      setSlots([]);
      setSlotsNote(
        "Choose an option for the service above to load the available times.",
      );
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      setSlotsLoading(true);
      setSlotsNote("");
      try {
        const base = getApiBaseUrl();
        // The chosen professional's own timezone wins over the page's default.
        const providerTimezone =
          providers.find((p) => String(p._id) === String(effectiveStaff))
            ?.timezone ||
          defaultTimezone ||
          null;
        const today = todayInTimezone(providerTimezone);
        const res = await axios.get(`${base}/api/bookings/available-slots`, {
          headers: getAuthHeaders(),
          params: {
            date: today,
            serviceId,
            staffId: effectiveStaff,
            // The RESOLVED length, not the catalog default: a 3-unit per-unit service
            // or a 2-hour booking occupies a different amount of the day, and the
            // slots offered have to match what is actually being booked. For a group
            // this is the WHOLE chain (primary then each member), so a slot is only
            // offered when everyone fits.
            ...(slotQueryDuration
              ? { serviceDuration: slotQueryDuration }
              : {}),
          },
        });
        const list =
          res.data?.availableSlots ||
          res.data?.slots ||
          (Array.isArray(res.data) ? res.data : []);
        if (cancelled) return;
        setSlots(list);
        // Trust the date the API resolved rather than the local guess, so the
        // booking is created for the same day the slots came from.
        setAppointmentDate(res.data?.date || today);
        // Slots are objects: { time24, time12, endTime }. `time24` is the value the
        // API expects for startTime; `time12` is what a person reads.
        const first = list.length
          ? list[0].time24 || (typeof list[0] === "string" ? list[0] : "")
          : "";
        setStartTime(first);
        if (!list.length) {
          // Name the actual rule instead of a vague "none available": same-day
          // slots start a few minutes out and have to FINISH before closing time,
          // so the last start is one service-length before close.
          setSlotsNote(
            "No bookable slots left today. Same-day slots start a few minutes from now and have to finish before the professional's closing time. You can still go ahead — set a start time below and you'll be asked to confirm that it's outside their normal hours.",
          );
        }
      } catch (err) {
        if (!cancelled) {
          setSlots([]);
          // Say WHY. "Schedule not found" means this professional has never set availability,
          // which is actionable; "unable to load" hides that and reads like a system fault.
          const reason = err?.response?.data?.message;
          setSlotsNote(
            reason
              ? `No slots could be offered: ${reason}`
              : "Unable to load this professional's slots.",
          );
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    serviceId,
    staffId,
    presetStaffId,
    services,
    providers,
    defaultTimezone,
    slotsRefreshKey,
    slotQueryDuration,
  ]);

  /**
   * Live countdown to the hold expiring.
   *
   * The hold is short (5 minutes by default) and now stops blocking the diary at
   * that exact moment, so "still waiting on payment" and "this slot has been given
   * back" are different situations that used to look identical on screen.
   */
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!isOpen || !created?.reservationExpiresAt) return undefined;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isOpen, created?.reservationExpiresAt]);

  const holdRemainingMs = created?.reservationExpiresAt
    ? Math.max(0, new Date(created.reservationExpiresAt).getTime() - nowMs)
    : null;
  const holdExpired = holdRemainingMs === 0;
  const holdLabel =
    holdRemainingMs === null
      ? ""
      : `${Math.floor(holdRemainingMs / 60000)}:${String(
          Math.floor((holdRemainingMs % 60000) / 1000),
        ).padStart(2, "0")}`;

  /**
   * STOP POLLING, AND INVALIDATE ANY CALLBACK ALREADY IN FLIGHT (2026-10-24).
   *
   * `clearInterval` stops FUTURE ticks but not one that is already awaiting its response, so a request that left
   * before the stop could still land and drive the screen. The generation counter is what makes a stale response
   * inert.
   */
  const stopPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = null;
  }, []);

  /** Poll the booking until it is paid, or until the session window lapses. */
  const startPolling = useCallback(
    (bookingId) => {
      pollStartRef.current = Date.now();
      // A new poll supersedes every earlier one.
      pollGenerationRef.current += 1;
      const generation = pollGenerationRef.current;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);

      pollTimerRef.current = setInterval(async () => {
        // SUPERSEDED: the modal moved on (paid, switched to cash, reset). Anything this callback would do to the
        // screen now belongs to a booking the desk is no longer looking at.
        if (generation !== pollGenerationRef.current) return;

        if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
          // Once a replacement time is on offer the hold no longer matters - the money
          // is already taken and someone has to answer. Stop the countdown erroring
          // over a question that is still waiting on a person.
          if (proposalPendingRef.current) return;
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setError(
            "This payment link has expired. You can close and start the walk-in again, or take cash instead.",
          );
          return;
        }

        try {
          const base = getApiBaseUrl();
          const res = await axios.get(`${base}/api/bookings/${bookingId}`, {
            headers: getAuthHeaders(),
          });
          const booking = res.data?.booking || res.data;
          const conflict = booking?.slotConflict || {};
          if (booking?.paymentStatus === "paid" || booking?.status === "confirmed") {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            proposalPendingRef.current = false;
            setProposal(null);
            setNeedsPlacement(false);
            // Settled: there is nothing left to resume, and leaving the entry behind
            // would offer the desk a walk-in that is already paid.
            clearPendingWalkIn();
            setResumable(null);

            // The system may have seated this walk-in somewhere OTHER than the time it
            // was created for (the slot went while the customer was paying, so it was
            // moved to the nearest free later slot). That is news the desk has to SEE:
            // confirming silently leaves them unable to tell the customer where they are
            // actually booked.
            //
            // The original time comes from the BOOKING, not from what this screen
            // happened to be holding in memory. An in-memory copy relies on this modal
            // having watched the whole sequence, so a reload, a second desk device or a
            // poll that arrives after the move all lose it - and a notice that appears
            // only sometimes is worse than none, because the desk learns to trust its
            // absence. `slotConflict.originalStartTime` is written server-side by the
            // resolver and survives all of that.
            const originalStart =
              conflict.originalStartTime ||
              booking?.originalStartTime ||
              createdStartRef.current;
            const finalStart = booking?.startTime || null;
            const finalDate = booking?.appointmentDate || null;
            const originalDate = conflict.originalDate || null;
            if (originalStart && finalStart && originalStart !== finalStart) {
              setMovedNotice({
                from: originalStart,
                to: finalStart,
                toEnd: booking?.endTime || null,
                fromDate: originalDate,
                toDate: finalDate,
              });
            }

            setStep("done");
            if (typeof onCompleted === "function") onCompleted(booking);
            return;
          }

          // RESUME CAN LAND ON A BOOKING THAT IS ALREADY GONE. A stored entry outlives
          // the walk-in it describes - the desk may have released it from the bookings
          // list from another device. Without this the modal sat on "Waiting for
          // payment" for the full 30-minute poll timeout, describing a slot that no
          // longer existed.
          if (booking?.status === "cancelled" || booking?.status === "expired") {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            proposalPendingRef.current = false;
            clearPendingWalkIn();
            setResumable(null);
            setCreated(null);
            setProposal(null);
            setNeedsPlacement(false);
            setStep("form");
            setError(
              "That walk-in is no longer waiting for payment — it was cancelled or the time was released. Nothing was charged, so start a new walk-in when you are ready.",
            );
            return;
          }

          // Paid, but the time was taken. There are two outcomes and they need
          // different screens:
          //
          //  - an OFFER exists (something else was free that day) -> ask accept/decline
          //  - NOTHING was free -> the desk sets the time. There is no slot to pick from
          //    (that is why it parked), so the desk supplies one and the professional
          //    has agreed to take the customer anyway. The customer's own screen just
          //    says a time is being arranged.
          if (
            booking?.status === "slot_conflict" &&
            conflict.resolution === "awaiting_customer" &&
            conflict.proposedDate &&
            conflict.proposedStartTime
          ) {
            proposalPendingRef.current = true;
            setNeedsPlacement(false);
            setProposal({
              proposedDate: conflict.proposedDate,
              proposedStartTime: conflict.proposedStartTime,
              proposedEndTime: conflict.proposedEndTime || null,
            });
          } else if (booking?.status === "slot_conflict") {
            // Keep polling: the customer's screen can be answered by the desk at any
            // moment, and the booking resolves out of slot_conflict when it is placed.
            proposalPendingRef.current = true;
            setProposal(null);
            setNeedsPlacement(true);
          }
        } catch (err) {
          // A 403/404 is NOT transient. Retrying one for the full 30 minutes hid a
          // real permission problem behind a screen that looked like "waiting" - the
          // customer paid and the desk was never told. Say so and stop.
          const status = err?.response?.status;
          if (status === 403 || status === 404) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            // 404 means the row is genuinely gone, so the stored entry is dead weight.
            // 403 does NOT: the booking still exists and is still holding its slot, the
            // caller simply cannot read it (they may have changed location).
            if (status === 404) {
              clearPendingWalkIn();
              setResumable(null);
            }
            setError(
              "The payment was submitted but its status could not be read. Close this and refresh the bookings list to check the booking.",
            );
            return;
          }
          // Transient failures are expected while the customer pays; keep trying.
        }
      }, POLL_INTERVAL_MS);
    },
    [onCompleted],
  );

  /**
   * Reattach to the walk-in this window was waiting on before it was interrupted.
   *
   * Deliberately does NOT trust the stored copy for anything but the booking id: the
   * poll reads the live booking, so an entry that has since been paid, cancelled or
   * relocated resolves to the correct screen within one tick. The stored time is used
   * only as a last-resort fallback for the moved-time notice, which now comes from the
   * booking itself.
   */
  const resumeWalkIn = useCallback(() => {
    const entry = resumable;
    if (!entry?.bookingId) {
      setResumable(null);
      clearPendingWalkIn();
      return;
    }
    setError("");
    setMovedNotice(null);
    setProposal(null);
    setNeedsPlacement(false);
    proposalPendingRef.current = false;
    createdStartRef.current = entry.startTime || null;
    setCreated({
      bookingId: entry.bookingId,
      stripeCheckoutUrl: entry.stripeCheckoutUrl || null,
      appointmentDate: entry.appointmentDate || null,
      startTime: entry.startTime || null,
      endTime: entry.endTime || null,
      totalAmount: entry.totalAmount || 0,
      reservationExpiresAt: entry.reservationExpiresAt || null,
    });
    setResumable(null);
    setStep("pay");
    startPolling(entry.bookingId);
  }, [resumable, startPolling]);

  /** Forget the stored walk-in without touching it — a wrong-till entry, usually. */
  const discardResumable = useCallback(() => {
    clearPendingWalkIn();
    setResumable(null);
  }, []);

  /**
   * A5 — "Take cash instead".
   *
   * A card declines, or the customer changes their mind. Before this the desk had to
   * release the walk-in and re-key everything.
   *
   * Two calls, deliberately, and in this order:
   *   1. the server cancels the CARD walk-in (expiring its Stripe session first, so a
   *      late card payment cannot be collected against a booking being replaced) and
   *      hands back the exact payload needed to reproduce the same service at the same
   *      price;
   *   2. we re-create through the ORDINARY walk-in route with `walkInChannel: "cash"`.
   *
   * Nothing here reconstructs pricing, and the create route's existing overlap detection
   * is what keeps the diary honest if somebody took the slot in the gap.
   */
  const switchToCash = useCallback(async () => {
    if (!created?.bookingId) return;
    setError("");
    setBusy(true);
    // Whether the card booking has ALREADY been cancelled. If it has, the desk cannot press "switch to cash"
    // again (409) or "cancel" (409) — so a failure after this point must put them back on the form with the
    // booking cleared, not leave them staring at a card walk-in that no longer exists (2026-10-24).
    let released = false;
    try {
      const base = getApiBaseUrl();
      const releasedResponse = await axios.post(
        `${base}/api/bookings/walk-in/${created.bookingId}/switch-to-cash`,
        {},
        { headers: getAuthHeaders() },
      );
      released = true;

      /**
       * THE OLD BOOKING IS GONE, SO ITS POLL MUST GO WITH IT (2026-10-24).
       *
       * The interval started when the payment link was created keeps fetching the CARD booking. That booking is now
       * `cancelled`, and the poll's cancelled-branch resets the modal to the form with "no longer waiting for
       * payment" — which is exactly what tore the cash screen down a few seconds after it appeared. Stopping here
       * (and bumping the generation) makes the switch stick.
       */
      stopPolling();

      const payload = releasedResponse.data?.createPayload;
      if (!payload?.serviceId) {
        setError(
          "The card walk-in was cancelled, but it could not be reproduced automatically. Start a new walk-in to take cash.",
        );
        setCreated(null);
        setStep("form");
        return;
      }

      // The card attempt is gone, so any stored copy of it is stale.
      clearPendingWalkIn();

      const createdCash = await axios.post(
        `${base}/api/bookings/walk-in`,
        {
          ...payload,
          walkInChannel: "cash",
          // The server now sends the parts, and a legacy row is split from `customerName`. This is the last
          // line of defence: if both are empty, the name already typed on this screen is used rather than
          // sending nothing and being refused.
          customerFirstName: payload.customerFirstName || firstName || undefined,
          customerLastName: payload.customerLastName || lastName || undefined,
        },
        { headers: getAuthHeaders() },
      );

      const data = createdCash.data || {};
      setReopenInfo(null);
      setMovedNotice(null);
      setProposal(null);
      setNeedsPlacement(false);
      createdStartRef.current = data.startTime || null;
      setChannel("cash");
      setCreated(data);
      setCashAmount(data.totalAmount ? String(data.totalAmount) : "");
      setLinkNotice(
        "Switched to cash. The card booking was cancelled and this is a new cash walk-in.",
      );
      setStep("pay");
    } catch (err) {
      const res = err?.response;
      const payload = res?.data || {};
      if (res?.status === 409 && payload.error === "availability_warning") {
        // The original time is no longer clean. The create route says exactly what is
        // wrong, and the desk decides — same prompt the normal create flow shows.
        setAvailabilityWarning(payload);
        setError(
          "The card booking was cancelled, but that time is no longer available. Choose a time below and create the cash walk-in.",
        );
        if (released) setCreated(null);
        setStep("form");
        return;
      }
      if (res?.status === 409 && payload.error === "overlap_warning") {
        setOverlapWarning(payload);
        setError(
          "The card booking was cancelled, but that time now clashes with another appointment. Confirm below to continue.",
        );
        if (released) setCreated(null);
        setStep("form");
        return;
      }
      // The card booking is already cancelled, so this screen holds a dead id: "switch to cash" and "cancel"
      // behind it would both be refused with 409. Clear it and send the desk back to the form.
      if (released) {
        setCreated(null);
        clearPendingWalkIn();
        setStep("form");
        setError(
          `${payload.message || "The cash walk-in could not be created."} The card walk-in was already cancelled, so it has been cleared — create the walk-in again to take cash.`,
        );
      } else {
        setError(
          payload.message ||
            "Unable to switch this walk-in to cash. Check the bookings list before trying again.",
        );
      }
    } finally {
      setBusy(false);
    }
  }, [created, firstName, lastName, stopPolling]);

  /**
   * Move straight on to the next customer, leaving this walk-in waiting on its own.
   *
   * A walk-in is paid by the customer on their own device, so the desk standing over the
   * screen is pure waste. The slot stays held, the booking stays visible in the
   * bookings list as "Awaiting payment", and the header chip reoffers it here.
   *
   * `reset()` deliberately does NOT touch sessionStorage, which is what keeps the
   * walk-in resumable after the form is cleared.
   */
  const startNextWalkIn = useCallback(() => {
    reset();
    setResumable(readPendingWalkIn());
  }, [reset]);

  /**
   * Reopen a walk-in the desk picked out of the bookings list.
   *
   * Sending the link is not the same as being paid, and the desk has to be able to get
   * back INTO a walk-in that is still holding a slot: to show the QR again, to re-send
   * the link, or to find out that the payment window has closed and the slot needs
   * releasing. Without this the only options were to wait on a screen already closed, or
   * to release the time the customer was still holding.
   *
   * The SERVER decides what is payable - a dead Stripe session can never be presented as
   * a live QR - so this only renders what it is told.
   *
   * Declared after `startPolling` on purpose, and depends on it: the effect is created
   * during render, so referencing `startPolling` any earlier would read it before its
   * `const` initialiser has run.
   */
  useEffect(() => {
    if (!isOpen || !reopenBookingId) return undefined;
    let cancelled = false;

    (async () => {
      setReopenLoading(true);
      setError("");
      try {
        const base = getApiBaseUrl();
        const res = await axios.get(
          `${base}/api/bookings/walk-in/${reopenBookingId}/payment`,
          { headers: getAuthHeaders() },
        );
        const data = res.data || {};
        if (cancelled) return;

        setReopenInfo(data);
        createdStartRef.current = data.startTime || null;
        setChannel(data.channel === "cash" ? "cash" : "card");
        // Seed the re-send fields from the booking, so "Send by email" shows WHERE it
        // is going. The server would fall back to the booking's own details anyway, but
        // an empty box next to a send button tells the desk nothing.
        setEmail(data.customerEmail || "");
        setPhone(data.customerPhone || "");
        setCreated({
          bookingId: data.bookingId,
          stripeCheckoutUrl: data.checkoutUrl || null,
          appointmentDate: data.appointmentDate || null,
          startTime: data.startTime || null,
          endTime: data.endTime || null,
          totalAmount: data.totalAmount || 0,
          amountPaid: data.amountPaid || 0,
          currency: data.currency || "USD",
          // The hold has usually lapsed by now - the desk is here to finish a payment,
          // not to watch a countdown that already hit zero.
          reservationExpiresAt: null,
        });
        // Resumable here too, so leaving via "Start another walk-in" keeps a chip for the
        // walk-in just reopened instead of losing it.
        savePendingWalkIn({
          bookingId: data.bookingId,
          stripeCheckoutUrl: data.checkoutUrl || null,
          startTime: data.startTime || null,
          endTime: data.endTime || null,
          appointmentDate: data.appointmentDate || null,
          reservationExpiresAt: null,
          totalAmount: data.totalAmount || 0,
          serviceName: data.serviceName || null,
        });
        setStep("pay");
        // Nothing to poll for once it is settled or the link is dead.
        if (data.state === "awaiting_payment") startPolling(data.bookingId);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            "Unable to reopen this walk-in. Close this and refresh the bookings list.",
        );
        setStep("form");
      } finally {
        if (!cancelled) setReopenLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, reopenBookingId, startPolling]);

  const createWalkIn = useCallback(
    async ({
      acknowledgeOverlap = false,
      acknowledgeAvailability = false,
    } = {}) => {
      setError("");
      setBusy(true);
      try {
        const base = getApiBaseUrl();
        const res = await axios.post(
          `${base}/api/bookings/walk-in`,
          {
            serviceId,
            ...(requireProvider ? { staffId } : {}),
            // F2: the engine's selection contract, plus the flat primitives the route
            // also accepts. Absent keys are dropped by JSON.stringify, so a fixed-price
            // service sends nothing meaningful here.
            selection: {
              quantity: selection.quantity,
              hours: selection.hours,
              tierId: selection.tierId,
            },
            quantity: selection.quantity,
            hours: selection.hours,
            tierId: selection.tierId,
            // Tips: a percentage is authoritative on the server; a custom amount is
            // sent as a flat figure. Only one of the two is ever non-zero.
            ...(tipIsCustom
              ? { tipAmount }
              : tipPercentage > 0
                ? { tipPercentage }
                : {}),
            ...(addOnIds.length
              ? {
                  serviceBundle: addOnIds.map((id) => ({
                    serviceId: id,
                    selection: addOnSelections[String(id)] || {},
                  })),
                }
              : {}),
            // Products sold at the desk. The server validates price and stock and the
            // card webhook performs the single authoritative stock deduction.
            ...(selectedProducts.length
              ? {
                  products: selectedProducts.map((line) => ({
                    productId: line.productId,
                    quantity: line.quantity,
                  })),
                }
              : {}),
            // Group members. Only sent when the delivering professional is an R3 and
            // the controls were actually shown, so a stale entry from a previous
            // professional can never ride along. Each member carries its OWN resolved
            // selection; the server chains their times after the primary customer.
            ...(canAddGroupMembers && groupMemberList.length
              ? {
                  groupMembers: groupMemberList.map((member) => ({
                    name: member.name,
                    phone: member.phone,
                    serviceId: member.serviceId,
                    selection: member.selection || {},
                  })),
                }
              : {}),
            customerFirstName: firstName,
            customerLastName: lastName,
            customerPhone: phone,
            customerEmail: email,
            walkInChannel: channel === "cash" ? "cash" : channel,
            // The location this walk-in belongs to. Sent explicitly for the front desk
            // so an owner can serve a site other than the one they are signed into;
            // the server ignores it for every other role and validates that the caller
            // owns the location and that the professional works there.
            ...(requireProvider && locationId ? { shopId: locationId } : {}),
            // Send the picked slot. Omitted when there was none, letting the
            // server fall back to its rounded "now" default.
            ...(startTime ? { startTime } : {}),
            ...(appointmentDate ? { appointmentDate } : {}),
            acknowledgeOverlap,
            acknowledgeAvailability,
          },
          { headers: getAuthHeaders() },
        );

        const data = res.data || {};
        setOverlapWarning(null);
        setAvailabilityWarning(null);
        setCreated(data);
        // Remember what we asked for, so the paid screen can tell the desk when the
        // system had to seat the walk-in somewhere else.
        createdStartRef.current = data.startTime || null;
        setCashAmount(data.totalAmount ? String(data.totalAmount) : "");
        // A cash walk-in is settled on the spot, so only a card one is worth resuming.
        // Survives a reload of this tab; see PENDING_WALKIN_KEY.
        setResumable(null);
        if (channel !== "cash" && data.bookingId) {
          savePendingWalkIn({
            bookingId: data.bookingId,
            stripeCheckoutUrl: data.stripeCheckoutUrl || null,
            sessionId: data.sessionId || null,
            startTime: data.startTime || null,
            endTime: data.endTime || null,
            appointmentDate: data.appointmentDate || null,
            reservationExpiresAt: data.reservationExpiresAt || null,
            totalAmount: data.totalAmount || 0,
            serviceName: data.serviceName || null,
          });
        } else {
          clearPendingWalkIn();
        }

        if (channel === "cash") {
          setStep("pay");
        } else {
          setStep("pay");
          if (data.bookingId) startPolling(data.bookingId);
        }
      } catch (err) {
        const status = err?.response?.status;
        const payload = err?.response?.data || {};

        if (status === 409 && payload.error === "overlap_warning") {
          // Not an error — the backend wants explicit confirmation.
          setOverlapWarning(payload);
          return;
        }

        // Almost/at closing time, or another non-clash availability condition.
        // The desk is warned and decides; they are the authority on the day.
        if (status === 409 && payload.error === "availability_warning") {
          setAvailabilityWarning(payload);
          return;
        }

        // The server's idea of "today" disagreed with ours (wrong device clock, or a
        // timezone the browser could not confirm). Adopt its date, re-fetch the slots
        // for that day and let staff pick again — never book the wrong day.
        if (payload.error === "walk_in_same_day_only" && payload.today) {
          setAppointmentDate(payload.today);
          setSlots([]);
          setStartTime("");
          setSlotsNote("");
          setSlotsRefreshKey((key) => key + 1);
          setError(
            `${payload.message} Reloading today's slots — pick a time and try again.`,
          );
          return;
        }
        // Surface the backend's `details` when it sends one: the generic message
        // alone is undiagnosable for the person standing at the desk.
        const base = payload.message || "Unable to create the walk-in. Please try again.";
        setError(payload.details ? `${base} (${payload.details})` : base);
      } finally {
        setBusy(false);
      }
    },
    [
      appointmentDate,
      addOnIds,
      addOnSelections,
      // Sent with the booking; omitting these would post a stale group list (or none)
      // after the professional or the members changed.
      canAddGroupMembers,
      groupMemberList,
      channel,
      email,
      firstName,
      lastName,
      phone,
      requireProvider,
      selection,
      selectedProducts,
      serviceId,
      staffId,
      startPolling,
      startTime,
      tipAmount,
      tipIsCustom,
      tipPercentage,
      // Sent as the booking's location for the front desk; the payload would otherwise
      // be built from a stale location after the picker changes.
      locationId,
    ],
  );

  const confirmCash = useCallback(async () => {
    if (!created?.bookingId) return;
    setBusy(true);
    setError("");
    try {
      const base = getApiBaseUrl();
      const res = await axios.post(
        `${base}/api/bookings/walk-in/${created.bookingId}/cash`,
        { amount: Number(cashAmount) },
        { headers: getAuthHeaders() },
      );
      setStep("done");
      if (typeof onCompleted === "function") onCompleted(res.data?.booking);
    } catch (err) {
      const payload = err?.response?.data || {};
      const base = payload.message || "Unable to confirm the cash payment.";
      setError(payload.details ? `${base} (${payload.details})` : base);
    } finally {
      setBusy(false);
    }
  }, [cashAmount, created, onCompleted]);

  const copyLink = useCallback(async () => {
    if (!created?.stripeCheckoutUrl) return;
    const url = created.stripeCheckoutUrl;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // The Clipboard API needs a secure origin. Fall back to a hidden
        // textarea so the button still works on an insecure/local device.
        const el = document.createElement("textarea");
        el.value = url;
        el.setAttribute("readonly", "");
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Unable to copy the link. Use Open payment page instead.");
    }
  }, [created]);

  const submitForm = useCallback(
    (event) => {
      event.preventDefault();
      if (!serviceId) return setError("Please choose a service.");
      if (requireProvider && !staffId)
        return setError("Please choose the professional delivering this walk-in.");
      // A line with no option chosen has no price and no length, so there is nothing
      // correct to create yet. Blocked here rather than sent for the server to refuse.
      if (selectionPending) {
        return setError(
          pendingSelectionLines.length > 1
            ? "Choose an option for every service before continuing."
            : "Choose an option for this service before continuing.",
        );
      }
      if (productsNeedCard) {
        return setError(
          "Products need a card payment — choose Scan to pay or Send a link, or remove the products. Cash covers the service only.",
        );
      }
      if (groupNeedsCard) {
        return setError(
          "Group members need a card payment — choose Scan to pay or Send a link, or remove the group members. A cash group walk-in cannot create each member's own booking, so it is not allowed.",
        );
      }
      // Each member becomes their own appointment, so an unnamed or service-less
      // member would create a booking nobody can identify or deliver.
      if (groupMemberList.length > 0) {
        const incomplete = groupMemberList.findIndex(
          (member) => !member.name?.trim() || !member.serviceId,
        );
        if (incomplete !== -1) {
          return setError(
            `Group member ${incomplete + 1} needs a name and a service.`,
          );
        }
      }
      if (groupSelectionPending) {
        return setError(
          "Choose an option for each group member's service before continuing.",
        );
      }
      if (!firstName.trim()) return setError("Customer first name is required.");
      // No free slots today means the desk chose the time, so it must actually be
      // supplied. Letting it through would silently default the booking to "now" - a
      // time nobody picked, for a professional who is squeezing the customer in.
      if (!slotsLoading && slots.length === 0 && !startTime) {
        return setError(
          "No free slots are left today, so set the time the professional will start.",
        );
      }
      // L3: both halves are required - the tick AND the explicit button. Creating the
      // booking holds the slot, so an unticked box must not create one.
      if (!intentAcknowledged) {
        return setError(
          "Tick \"I'm aware this will create a walk-in\" to continue — it holds the time slot.",
        );
      }
      setError("");
      createWalkIn();
    },
    [
      createWalkIn,
      firstName,
      groupMemberList,
      groupNeedsCard,
      groupSelectionPending,
      intentAcknowledged,
      pendingSelectionLines.length,
      productsNeedCard,
      requireProvider,
      selectionPending,
      serviceId,
      slots.length,
      slotsLoading,
      staffId,
      startTime,
    ],
  );

  if (!isOpen) return null;

  // Rendered into <body>, NOT inline where the button sits.
  //
  // The booth-renter dashboard renders its header with `color: white` and the walk-in
  // button lives inside it, so an inline modal inherited white text: every label and
  // heading vanished against the modal's own white background and the dialog looked
  // blank. A portal takes the modal out of the page's DOM and its cascade entirely, so
  // no host page can restyle it by accident.
  return createPortal(
    <div className="walkin-overlay" role="dialog" aria-modal="true">
      <div className="walkin-modal">
        <header className="walkin-header">
          <h2>New walk-in</h2>
          {/* An interrupted walk-in, kept OUT of the form on purpose. Reopening
              `+ Walk-in` normally means "take the next customer", so this must never
              push the form around or sit between its fields. Offered, never
              auto-restored - silently landing the desk on an old payment screen would
              hide a slot still being held for somebody. */}
          {resumable ? (
            <div className="walkin-resume-chip">
              <span className="walkin-resume-dot" aria-hidden="true" />
              <span className="walkin-resume-text">
                Awaiting payment
                {formatClockTime(resumable.startTime)
                  ? ` · ${formatClockTime(resumable.startTime)}`
                  : ""}
              </span>
              <button
                type="button"
                className="walkin-resume-go"
                onClick={resumeWalkIn}
              >
                Resume
              </button>
              <button
                type="button"
                className="walkin-resume-x"
                onClick={discardResumable}
                aria-label="Dismiss this walk-in awaiting payment"
              >
                ×
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="walkin-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error ? <div className="walkin-error">{error}</div> : null}

        {/* ---------------- Step 1: details ---------------- */}
        {step === "form" && reopenLoading ? (
          // A reopen is in flight. Showing the CREATE form here would flash a blank
          // walk-in form in front of a desk that asked to finish an existing payment.
          <div className="walkin-body">
            <h3>Opening this walk-in…</h3>
            <p className="walkin-muted">
              Checking whether the payment link is still live.
            </p>
          </div>
        ) : null}

        {step === "form" && !reopenLoading ? (
          <form className="walkin-body" onSubmit={submitForm}>
            {requireProvider ? (
              <>
                {/* R1 only. Always shown when there is a location at all, because a
                    desk taking money should be able to SEE where the booking lands
                    even when there is nothing to choose; it is read-only in that
                    case. R2 is pinned to a single location and is never asked. */}
                {canChooseLocation && locations.length >= 1 ? (
                  <label className="walkin-field">
                    <span>Location *</span>
                    <select
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                      disabled={locations.length < 2}
                    >
                      {locations.map((loc) => (
                        <option key={loc._id} value={loc._id}>
                          {/* CITY AND STATE, NOT THE BUSINESS NAME (user, 2026-10-24). Two branches of one business
                              share a name, so the name cannot tell the desk WHERE the walk-in lands. */}
                          {loc.city && loc.state
                            ? `${loc.city}, ${String(loc.state).toUpperCase()}`
                            : loc.city || loc.name}
                        </option>
                      ))}
                    </select>
                    <small>
                      {locations.length < 2
                        ? "Your only location — a business with more locations can serve any of them from here."
                        : "The professional list below follows the location you pick."}
                    </small>
                  </label>
                ) : null}

                <label className="walkin-field">
                  <span>Professional delivering this walk-in *</span>
                  <select
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                  >
                    <option value="">
                      {providersLoading
                        ? "Loading professionals…"
                        : "Select a professional…"}
                    </option>
                    {providers.map((p) => (
                      // The name lives on the populated `userId`, NOT on the staff row
                      // itself — reading p.firstName directly renders an empty option
                      // for every professional.
                      <option key={p._id} value={p._id}>
                        {`${p.userId?.firstName || ""} ${p.userId?.lastName || ""}`.trim() ||
                          p.userId?.email ||
                          "Unnamed professional"}
                      </option>
                    ))}
                  </select>
                  <small>
                    Owners and admins can't be the provider of record — the commission
                    split follows whoever delivers the service.
                  </small>
                </label>
              </>
            ) : null}

            <label className="walkin-field">
              <span>Service *</span>
              <select
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
              >
                <option value="">
                  {servicesLoading
                    ? "Loading services…"
                    : requireProvider && !staffId
                      ? "Choose a professional first…"
                      : "Select a service…"}
                </option>
                {serviceList.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                    {s.price ? ` — ${s.price}` : ""}
                  </option>
                ))}
              </select>
            </label>

            {selectedService ? (
              <ServicePricingSelector
                service={selectedService}
                selection={selection}
                onChange={(next) =>
                  setSelection((prev) => ({ ...prev, ...next }))
                }
                isMobile
              />
            ) : null}

            {/* Add-ons: this professional's OTHER services. Each adds its own price and
                length, so the slot list is rebuilt from the combined appointment. */}
            {selectedService && serviceList.length > 1 ? (
              <div className="walkin-field">
                <span>Add-ons</span>
                <div className="walkin-addons">
                  {serviceList
                    .filter(
                      (s) => String(s._id) !== String(selectedService._id),
                    )
                    .map((s) => {
                      const id = String(s._id);
                      return (
                        <label key={id} className="walkin-addon">
                          <input
                            type="checkbox"
                            checked={addOnIds.includes(id)}
                            onChange={() => toggleAddOn(id)}
                          />
                          <span>{s.name}</span>
                        </label>
                      );
                    })}
                </div>
                <small>Optional. Only this professional's services are listed.</small>
              </div>
            ) : null}

            {/* An add-on that needs a choice gets its OWN selector, like the cart. */}
            {addOnIds.map((id) => {
              const addOnService = serviceList.find(
                (s) => String(s._id) === String(id),
              );
              if (!addOnService) return null;
              return (
                <div key={id} className="walkin-field">
                  <span>{addOnService.name}</span>
                  <ServicePricingSelector
                    service={addOnService}
                    selection={addOnSelections[id] || {}}
                    onChange={(next) =>
                      setAddOnSelections((prev) => ({
                        ...prev,
                        [id]: { ...(prev[id] || {}), ...next },
                      }))
                    }
                    isMobile
                  />
                </div>
              );
            })}

            <div className="walkin-row">
              <label className="walkin-field">
                <span>First name *</span>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="walkin-field">
                <span>Last name</span>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </div>

            <div className="walkin-row">
              <label className="walkin-field">
                <span>Phone</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="walkin-field">
                <span>Email</span>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </div>

            {/* Group members - placed AFTER the primary customer's own details so the
                desk fills in the person in the chair first. R3 providers only, and
                hidden entirely for R4/R7 so the desk is never offered something the
                API would refuse. Each member becomes their OWN appointment, timed
                straight after the customer above, which is why no times are entered
                here.
                Mirrors the customer booking page: a "Who" field plus relationship
                shortcuts that fill it in. There is deliberately NO phone field - a
                member's own sibling booking inherits the primary customer's contact
                details, so anything typed there would never be used. */}
            {canAddGroupMembers ? (
              <div className="walkin-field">
                <span>Group members (optional)</span>
                <small>
                  Each member gets their own appointment, booked straight after the
                  customer above. Card payment only.
                </small>

                {groupMemberList.map((member, index) => {
                  const memberService = serviceList.find(
                    (s) => String(s._id) === String(member.serviceId),
                  );
                  return (
                    <div key={`member-${index}`} className="walkin-member">
                      <div className="walkin-member-head">
                        <strong>Member {index + 1}</strong>
                        <button
                          type="button"
                          className="walkin-member-remove"
                          onClick={() => removeGroupMember(index)}
                        >
                          Remove
                        </button>
                      </div>

                      <label className="walkin-field">
                        <span>Who *</span>
                        <input
                          value={member.name}
                          onChange={(e) =>
                            updateGroupMember(index, { name: e.target.value })
                          }
                          placeholder="Enter name"
                          autoComplete="off"
                        />
                      </label>
                      <div className="walkin-relations">
                        {GROUP_RELATIONSHIP_OPTIONS.map((relationship) => (
                          <button
                            key={relationship}
                            type="button"
                            className={
                              member.name === relationship
                                ? "walkin-relation selected"
                                : "walkin-relation"
                            }
                            onClick={() =>
                              updateGroupMember(index, { name: relationship })
                            }
                          >
                            {relationship}
                          </button>
                        ))}
                      </div>

                      <label className="walkin-field">
                        <span>Service *</span>
                        <select
                          value={member.serviceId}
                          onChange={(e) =>
                            // Units do not carry across services, so changing the
                            // service resets that member's OWN selection.
                            updateGroupMember(index, {
                              serviceId: e.target.value,
                              selection: {},
                            })
                          }
                        >
                          <option value="">Select a service…</option>
                          {serviceList.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s.name}
                              {s.price ? ` — ${s.price}` : ""}
                            </option>
                          ))}
                        </select>
                      </label>

                      {memberService ? (
                        <ServicePricingSelector
                          service={memberService}
                          selection={member.selection || {}}
                          onChange={(next) =>
                            updateGroupMember(index, {
                              selection: {
                                ...(member.selection || {}),
                                ...next,
                              },
                            })
                          }
                          isMobile
                        />
                      ) : null}
                    </div>
                  );
                })}

                {groupMemberList.length < MAX_ADDITIONAL_GROUP_MEMBERS ? (
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={addGroupMember}
                  >
                    + Add group member
                  </button>
                ) : (
                  <small>
                    Up to {MAX_ADDITIONAL_GROUP_MEMBERS} group members per walk-in, the
                    same limit as booking online. Create a separate walk-in for anyone
                    else.
                  </small>
                )}
              </div>
            ) : null}

            {/* Slot picker. Chosen here, BEFORE anything is created, so the slot
                that gets held is the one the staff member actually picked. */}
            {(staffId || presetStaffId) ? (
              <div className="walkin-field">
                <span>Time slot *</span>
                <small>
                  Today only{appointmentDate ? ` (${appointmentDate})` : ""} — a walk-in
                  cannot be booked for another day.
                </small>
                {slotsLoading ? (
                  <p className="walkin-muted">Loading today's slots…</p>
                ) : slots.length > 0 ? (
                  <div className="walkin-slots">
                    {slots.map((slot) => {
                      // { time24, time12, endTime } — time24 is sent to the API,
                      // time12 is displayed. Rendering the object directly is what
                      // threw "Objects are not valid as a React child".
                      const value =
                        typeof slot === "string" ? slot : slot.time24;
                      const label =
                        typeof slot === "string"
                          ? slot
                          : slot.time12 || slot.time24;
                      if (!value) return null;
                      return (
                        <button
                          key={value}
                          type="button"
                          className={
                            value === startTime
                              ? "walkin-slot selected"
                              : "walkin-slot"
                          }
                          onClick={() => setStartTime(value)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <p className="walkin-muted">
                      {slotsNote || "No free slots left today."}
                    </p>

                    {/* NOTHING FREE TODAY - so the desk states the time instead.
                        The professional is willing to take the customer outside their
                        normal availability, which is a decision only they can make, so
                        this is a REQUIRED field rather than a nice-to-have: defaulting
                        to "now" would put a time in the diary that nobody chose.

                        Shown ONLY in this case. When slots exist the chips above are the
                        choice, and an extra free-text time box would just invite a figure
                        nobody validated. */}
                    <label className="walkin-field">
                      <span>Start time *</span>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                      <small>
                        This professional has no free slots left today, so they are
                        taking this customer outside their normal availability. Enter the
                        time they will actually start — you'll be asked to confirm if it
                        overlaps another appointment.
                      </small>
                    </label>
                  </>
                )}
              </div>
            ) : null}

            {/* Products. Sourced from the booking-scoped endpoint, so a booth renter or
                freelancer is offered only their own stock. */}
            {productOptions.length > 0 ? (
              <div className="walkin-field">
                <span>Products (optional)</span>
                <div className="walkin-products">
                  {productOptions.map((product) => {
                    const quantity = productQuantities[product.productId] || 0;
                    return (
                      <div
                        key={product.productId}
                        className={
                          quantity > 0
                            ? "walkin-product selected"
                            : "walkin-product"
                        }
                      >
                        <div className="walkin-product-info">
                          {product.imageUrl ? (
                            <img
                              className="walkin-product-thumb"
                              src={product.imageUrl}
                              alt=""
                              loading="lazy"
                            />
                          ) : (
                            // Keeps every tile the same height whether or not the product
                            // has a photo, so the grid rows stay flush.
                            <span
                              className="walkin-product-thumb walkin-product-thumb--empty"
                              aria-hidden="true"
                            />
                          )}
                          <div className="walkin-product-text">
                            <span>
                              {product.name}
                              {product.variantLabel
                                ? ` — ${product.variantLabel}`
                                : ""}
                            </span>
                            <small>${product.price.toFixed(2)}</small>
                            <small>{product.stock} in stock</small>
                          </div>
                        </div>
                        <div className="walkin-product-qty">
                          <button
                            type="button"
                            onClick={() =>
                              setProductQuantity(
                                product.productId,
                                quantity - 1,
                              )
                            }
                            disabled={quantity <= 0}
                            aria-label={`Remove one ${product.name}`}
                          >
                            −
                          </button>
                          <span>{quantity}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setProductQuantity(
                                product.productId,
                                quantity + 1,
                              )
                            }
                            disabled={quantity >= product.stock}
                            aria-label={`Add one ${product.name}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <small>
                  Stock is deducted when the payment completes, not when the walk-in is
                  created.
                </small>
              </div>
            ) : null}

            {/* Payment method sits AFTER the products deliberately: products and group
                members both constrain the channel (neither can be cash), so the desk
                makes the choices first and the restriction is explained right here
                rather than appearing as a surprise after the fact. */}
            <div className="walkin-field">
              <span>How will they pay?</span>
              <div className="walkin-channels">
                {[
                  { id: "qr", label: "Scan to pay" },
                  { id: "link", label: "Send a link" },
                  { id: "cash", label: "Cash at desk" },
                ].map((option) => (
                  <label key={option.id} className="walkin-radio">
                    <input
                      type="radio"
                      name="walkin-channel"
                      value={option.id}
                      checked={channel === option.id}
                      onChange={() => setChannel(option.id)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <small>
                Card payments go through Stripe and confirm themselves. Cash is
                confirmed by you.
              </small>
            </div>

            {/* Told as soon as it becomes true, rather than only on submit. */}
            {productsNeedCard ? (
              <div className="walkin-inline-warning">
                Products need a card payment. Choose “Scan to pay” or “Send a link”, or
                remove the products — cash covers the service only.
              </div>
            ) : null}

            {groupNeedsCard ? (
              <div className="walkin-inline-warning">
                Group members need a card payment. Each member gets their own booking,
                and only a card payment creates those — choose “Scan to pay” or “Send a
                link”, or remove the group members.
              </div>
            ) : null}

            {/* Products are taxed by Stripe, services are not. The rate comes from THIS
                LOCATION's address - a walk-in is an in-person sale, so the tax is owed
                where the sale happens, and pinning it that way also means the customer
                is never asked for a billing address at the counter. The exact figure is
                still only known once Stripe has calculated it, so the desk is warned
                rather than shown a number that could contradict Stripe's own page. */}
            {selectedProducts.length > 0 ? (
              <div className="walkin-notice">
                Sales tax on the products is calculated from this location&rsquo;s
                address and added at payment. Services are not taxed, so the amount
                charged may be a little more than the total shown here.
              </div>
            ) : null}

            {/* What is being booked. The MONEY is in the cost summary below, so this
                line states the service and the length only — the desk reads one figure,
                not three. */}
            {selectedService ? (
              <p className="walkin-summary">
                {resolvedBundle.lines
                  .map((line) => line?.label)
                  .filter(Boolean)
                  .join(" + ") || selectedService.name}
                {effectiveDuration ? ` · ${effectiveDuration} min` : ""}
              </p>
            ) : null}

            {/* ── WHAT THE CUSTOMER OWES, BEFORE THEY ARE ASKED TO PAY ─────────────────────
                A walk-in is a sale at a counter: the person in front of the desk has to be
                told the amount, and the desk has to be able to read it out. The modal used
                to show the service price buried in one line of running text and never
                stated a total. Rows appear only for what is actually in the sale. */}
            {effectivePrice > 0 ? (
              <div className="walkin-cost-summary">
                <div className="walkin-cost-row">
                  <span>
                    {groupMemberList.length > 0
                      ? "Service (customer)"
                      : "Service"}
                  </span>
                  <span>${effectivePrice.toFixed(2)}</span>
                </div>
                {groupMembersTotal > 0 ? (
                  <div className="walkin-cost-row">
                    <span>
                      {groupMemberList.length} group member
                      {groupMemberList.length === 1 ? "" : "s"}
                    </span>
                    <span>${groupMembersTotal.toFixed(2)}</span>
                  </div>
                ) : null}
                {productsTotal > 0 ? (
                  <div className="walkin-cost-row">
                    <span>Products</span>
                    <span>${productsTotal.toFixed(2)}</span>
                  </div>
                ) : null}
                {tipAmount > 0 ? (
                  <div className="walkin-cost-row">
                    <span>
                      Tip
                      {!tipIsCustom && tipPercentage
                        ? ` (${tipPercentage}%)`
                        : ""}
                    </span>
                    <span>${tipAmount.toFixed(2)}</span>
                  </div>
                ) : null}
                <div className="walkin-cost-row total">
                  <span>Total</span>
                  <span>
                    $
                    {(
                      effectivePrice +
                      groupMembersTotal +
                      productsTotal +
                      tipAmount
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            ) : null}

            {/* Tips. Same percentages as the customer pages, priced on the SERVICE only.
                The desk adds this at the counter because the customer is right there. */}
            {effectivePrice > 0 ? (
              <div className="walkin-field">
                <span>Tip</span>
                <div className="walkin-tips">
                  {TIP_PRESET_PERCENTAGES.map((percent) => (
                    <button
                      key={percent}
                      type="button"
                      className={
                        !tipIsCustom && tipPercentage === percent
                          ? "walkin-tip selected"
                          : "walkin-tip"
                      }
                      onClick={() => {
                        setTipIsCustom(false);
                        setTipPercentage(percent);
                      }}
                    >
                      {`${percent}%`}
                      <small>
                        ${((effectivePrice * percent) / 100).toFixed(2)}
                      </small>
                    </button>
                  ))}
                  <button
                    type="button"
                    className={tipIsCustom ? "walkin-tip selected" : "walkin-tip"}
                    onClick={() => setTipIsCustom(true)}
                  >
                    Custom
                    <small>Your amount</small>
                  </button>
                </div>
                {tipIsCustom ? (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={customTip}
                    onChange={(e) => setCustomTip(e.target.value)}
                    placeholder="0.00"
                    aria-label="Custom tip amount"
                  />
                ) : null}
                <small>Calculated on the service only — not on add-ons or products.</small>
              </div>
            ) : null}

            <label className="walkin-intent">
              <input
                type="checkbox"
                checked={intentAcknowledged}
                onChange={(e) => setIntentAcknowledged(e.target.checked)}
              />
              <span>
                I'm aware this will create a walk-in
                <small>
                  A booking is written and the time slot is held for a few minutes
                  while the customer pays.
                </small>
              </span>
            </label>

            {overlapWarning ? (
              <div className="walkin-overlap">
                <strong>Possible double-booking</strong>
                <p>
                  {overlapWarning.message ||
                    "This time overlaps another appointment for this professional."}
                </p>
                <div className="walkin-actions">
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={() => setOverlapWarning(null)}
                  >
                    Go back
                  </button>
                  <button
                    type="button"
                    className="walkin-btn"
                    disabled={busy}
                    onClick={() => createWalkIn({ acknowledgeOverlap: true })}
                  >
                    Continue anyway
                  </button>
                </div>
              </div>
            ) : availabilityWarning ? (
              <div className="walkin-overlap">
                <strong>
                  {availabilityWarning.code === "outside_working_hours"
                    ? "This is outside their working hours"
                    : "This is outside their normal availability"}
                </strong>
                <p>
                  {availabilityWarning.message ||
                    "This time is not normally bookable for this professional."}
                </p>
                <p className="walkin-muted">
                  Book it if the service is really happening now — you are the
                  authority on the day.
                </p>
                <div className="walkin-actions">
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={() => setAvailabilityWarning(null)}
                  >
                    Go back
                  </button>
                  <button
                    type="button"
                    className="walkin-btn"
                    disabled={busy}
                    onClick={() =>
                      createWalkIn({
                        acknowledgeOverlap: true,
                        acknowledgeAvailability: true,
                      })
                    }
                  >
                    Book it anyway
                  </button>
                </div>
              </div>
            ) : (
              <div className="walkin-actions">
                <button
                  type="button"
                  className="walkin-btn ghost"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="walkin-btn"
                  disabled={busy || !intentAcknowledged}
                  title={
                    intentAcknowledged
                      ? undefined
                      : "Tick the confirmation box above to continue"
                  }
                >
                  {busy ? "Working…" : "Create walk-in & hold slot"}
                </button>
              </div>
            )}
          </form>
        ) : null}

        {/* ---------------- Step 2: collect ---------------- */}
        {step === "pay" ? (
          <div className="walkin-body">
            {channel === "cash" ? (
              <>
                <h3>Cash payment</h3>
                <p className="walkin-muted">
                  {selectedService?.name}
                  {created?.totalAmount ? ` · ${created.totalAmount}` : ""}
                </p>
                {holdLabel ? (
                  <p className={holdExpired ? "walkin-hold expired" : "walkin-hold"}>
                    {holdExpired
                      ? "The hold has expired, so this time is free for other bookings again. Confirm the cash now to keep it, or cancel the walk-in."
                      : `Slot held for ${holdLabel}. After that it is released to other bookings.`}
                  </p>
                ) : null}
                <label className="walkin-field">
                  <span>Amount received *</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                  />
                </label>
                <div className="walkin-actions">
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={() => setConfirmRelease(true)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="walkin-btn"
                    disabled={busy || !cashAmount}
                    onClick={confirmCash}
                  >
                    {busy ? "Confirming…" : "Confirm cash received"}
                  </button>
                </div>

                {/* The prompt must render in the CASH step too. It previously lived
                    only in the card branch, so from here Cancel and X set the flag
                    and nothing appeared — the buttons looked dead. */}
                {confirmRelease ? (
                  <div className="walkin-overlap">
                    <strong>Cancel this walk-in and release the time?</strong>
                    <p>
                      Nothing has been collected, so the slot goes back for other
                      bookings. Use “Confirm cash received” if the money has changed
                      hands.
                    </p>
                    <div className="walkin-actions">
                      <button
                        type="button"
                        className="walkin-btn ghost"
                        onClick={() => setConfirmRelease(false)}
                      >
                        Keep it
                      </button>
                      <button
                        type="button"
                        className="walkin-btn"
                        onClick={releaseWalkIn}
                      >
                        Release slot &amp; cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : reopenInfo?.state === "paid" ? (
              <>
                <h3>Already paid ✓</h3>
                <p className="walkin-muted">
                  This walk-in has been paid for, so there is nothing left to collect.
                </p>
                <div className="walkin-actions">
                  <button
                    type="button"
                    className="walkin-btn"
                    onClick={startNextWalkIn}
                  >
                    Start another walk-in
                  </button>
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={handleClose}
                  >
                    Done
                  </button>
                </div>
              </>
            ) : reopenInfo?.state === "link_dead" ? (
              <>
                <h3>This payment link has ended</h3>
                {/* Deliberately offers NO QR or resend. The server read the live
                    Stripe session and it is not payable, so showing a code here would
                    invite the desk to send a customer to a dead page. */}
                <div className="walkin-inline-warning">
                  The card payment window for this walk-in has closed, so nothing can be
                  paid on the original link any more. Release the walk-in and create it
                  again, or take cash at the desk.
                </div>
                <div className="walkin-actions">
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={handleClose}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="walkin-btn"
                    onClick={() => setConfirmRelease(true)}
                  >
                    Release the slot
                  </button>
                </div>
                {confirmRelease ? (
                  <div className="walkin-overlap">
                    <strong>Release this walk-in?</strong>
                    <p>
                      The slot goes back for other bookings. Create the walk-in again if
                      the customer still wants to pay by card.
                    </p>
                    <div className="walkin-actions">
                      <button
                        type="button"
                        className="walkin-btn ghost"
                        onClick={() => setConfirmRelease(false)}
                      >
                        Keep it
                      </button>
                      <button
                        type="button"
                        className="walkin-btn"
                        onClick={releaseWalkIn}
                      >
                        Release slot &amp; cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <h3>Waiting for payment</h3>
                <p className="walkin-muted">
                  This screen updates on its own — there's nothing to tap afterwards.
                </p>

                {reopenInfo?.state === "awaiting_payment" ? (
                  <div className="walkin-notice">
                    <strong>Reopened.</strong> This walk-in is still awaiting payment.
                    Show the QR again, or re-send the link if the customer needs it.
                  </div>
                ) : null}
                {holdLabel && !needsPlacement ? (
                  <p className={holdExpired ? "walkin-hold expired" : "walkin-hold"}>
                    {holdExpired
                      ? "The hold has expired, so this time is free for other bookings again. If they pay now it may still be honoured — otherwise cancel the walk-in and start again."
                      : `Slot held for ${holdLabel}. After that it is released to other bookings.`}
                  </p>
                ) : null}

                {/* Paid, but the time was taken. The offer is shown HERE and on the
                    customer's own screen; whichever answers first wins, and the other
                    updates on its next poll. */}
                {proposal ? (
                  <div className="walkin-overlap">
                    <strong>That time was taken — here is the next available</strong>
                    <p>
                      <strong>
                        {formatProposalLabel(
                          proposal.proposedDate,
                          proposal.proposedStartTime,
                          proposal.proposedEndTime,
                        )}
                      </strong>
                    </p>
                    <p className="walkin-muted">
                      Ask the customer whether that works for them. Either screen can
                      answer this — tap Accept here, or let them accept on their own
                      phone.
                    </p>
                    <div className="walkin-actions">
                      <button
                        type="button"
                        className="walkin-btn ghost"
                        disabled={busy}
                        onClick={() => respondToProposal(false)}
                      >
                        Decline &amp; refund
                      </button>
                      <button
                        type="button"
                        className="walkin-btn"
                        disabled={busy}
                        onClick={() => respondToProposal(true)}
                      >
                        {busy ? "Working…" : "Accept new time"}
                      </button>
                    </div>
                  </div>
                ) : null}

                {/* PAID, and there is no free slot left to move them into (Part 4).
                    There is nothing to OFFER, which is why the booking parked - so the
                    desk states the time, and the professional has agreed to take the
                    customer outside their normal availability. The customer's own
                    screen already says a time is being arranged.

                    Two steps on purpose: the first attempt surfaces the overlap, and
                    "Place anyway" is a deliberate second action. The money is already
                    taken, so a collision must not be waved through by accident. */}
                {needsPlacement ? (
                  <div className="walkin-overlap">
                    <strong>Payment received — no free slot left today</strong>
                    <p className="walkin-muted">
                      The time they booked was taken while they were paying, and this
                      professional has nothing else free today. Enter the time they will
                      actually start.
                    </p>

                    <label className="walkin-field">
                      <span>Start time *</span>
                      <input
                        type="time"
                        value={placementTime}
                        onChange={(e) => {
                          setPlacementTime(e.target.value);
                          setPlacementConflict(null);
                        }}
                      />
                    </label>

                    {placementConflict ? (
                      <div className="walkin-inline-warning">
                        {placementConflict}
                      </div>
                    ) : null}

                    <div className="walkin-actions">
                      <button
                        type="button"
                        className="walkin-btn"
                        disabled={busy || !placementTime}
                        onClick={() => placeWalkIn()}
                      >
                        {busy ? "Working…" : "Place at this time"}
                      </button>
                      {placementConflict ? (
                        <button
                          type="button"
                          className="walkin-btn ghost"
                          disabled={busy}
                          onClick={() => placeWalkIn({ acknowledgeOverlap: true })}
                        >
                          Place anyway
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {/* Scannable QR so the customer pays on their own device. The
                    value is only ever the Stripe checkout URL, so the raw URL is
                    no longer displayed — staff only need the code.

                    The PATTERN stays square: a circular QR pattern stops scanning.
                    The branding is the circular frame plus brand-coloured modules,
                    which keep contrast high enough to scan reliably. */}
                {!proposal && !needsPlacement && created?.stripeCheckoutUrl ? (
                  <div className="walkin-qr-frame">
                    <div className="walkin-qr">
                      <QRCodeCanvas
                        value={created.stripeCheckoutUrl}
                        size={188}
                        level="M"
                        fgColor="#2d6da3"
                        bgColor="#ffffff"
                      />
                    </div>
                    <p className="walkin-qr-caption">
                      Ask the customer to scan this with their phone camera
                    </p>
                  </div>
                ) : null}

                {/* The slot is only held once the walk-in exists, so recording it
                    BEFORE the service is what keeps the diary honest. */}
                <p className="walkin-guidance">
                  <strong>Start the walk-in before the service begins.</strong> This
                  holds the time slot while the customer pays. If it's only recorded
                  afterwards, an online booking could take the slot in the meantime.
                </p>

                {/* The payment link is delivered by OUR backend (Mailgun /
                    Telnyx). The recipient fields live here too, because staff
                    often only get the customer's email or phone after the
                    walk-in has been created. */}
                <div className="walkin-row">
                  <label className="walkin-field">
                    <span>Customer phone</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 555 010 1234"
                      autoComplete="off"
                    />
                  </label>
                  <label className="walkin-field">
                    <span>Customer email</span>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      autoComplete="off"
                    />
                  </label>
                </div>

                <div className="walkin-actions one-row">
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={copyLink}
                  >
                    {copied ? "Copied ✓" : "Copy link"}
                  </button>
                  <a
                    className="walkin-btn ghost"
                    href={created?.stripeCheckoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open payment page
                  </a>
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    disabled={busy}
                    onClick={() => sendPaymentLink("sms")}
                  >
                    Send by SMS
                  </button>
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    disabled={busy}
                    onClick={() => sendPaymentLink("email")}
                  >
                    Send by email
                  </button>
                </div>

                {linkNotice ? (
                  <p className="walkin-notice">{linkNotice}</p>
                ) : null}

                {/* THE DESK IS NOT THE ONE WAITING. The customer pays on their own
                    phone, so standing over this screen wastes the till. Saying where the
                    walk-in can be found afterwards is the part that makes leaving it
                    safe rather than merely possible. */}
                <p className="walkin-guidance">
                  <strong>You do not have to wait for this.</strong> The customer pays on
                  their own device. It stays under <strong>Bookings → Awaiting
                  payment</strong> until they do, and this window reoffers it at the top
                  while the slot is still held. Take the next customer now.
                </p>

                <div className="walkin-actions">
                  {/* A5: the card is not working and the customer wants to pay cash.
                      Shown only when there IS a live card session to replace - a cash
                      walk-in has none, and once a payment is settled or the time was
                      taken this is not the right control any more.
                      Products and group members are refused server-side (both require a
                      card), so the click is always explainable if it is rejected. */}
                  {created?.stripeCheckoutUrl &&
                  !proposal &&
                  !needsPlacement ? (
                    <button
                      type="button"
                      className="walkin-btn ghost"
                      disabled={busy}
                      onClick={switchToCash}
                    >
                      Take cash instead
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="walkin-btn ghost"
                    onClick={() => setConfirmRelease(true)}
                  >
                    Cancel walk-in
                  </button>
                  <button
                    type="button"
                    className="walkin-btn"
                    onClick={startNextWalkIn}
                  >
                    Start another walk-in
                  </button>
                </div>

                {confirmRelease ? (
                  <div className="walkin-overlap">
                    <strong>Cancel this walk-in and release the time?</strong>
                    <p>
                      It has not been paid for, so the slot goes back for other bookings
                      and the payment link stops working. Leave it alone if the customer
                      is still paying — it stays on the bookings list either way.
                    </p>
                    <div className="walkin-actions">
                      <button
                        type="button"
                        className="walkin-btn ghost"
                        onClick={() => setConfirmRelease(false)}
                      >
                        Keep it
                      </button>
                      <button
                        type="button"
                        className="walkin-btn"
                        onClick={releaseWalkIn}
                      >
                        Release slot &amp; cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {/* ---------------- Step 3: done ---------------- */}
        {step === "done" ? (
          <div className="walkin-body">
            <h3>Paid ✓</h3>
            {/* The time moved, so say so PROPERLY. This is the desk's only signal that
                the appointment is no longer at the time they set, and the customer's own
                screen shows the same change. A side-by-side "was / now" reads at a
                glance; wording it as a sentence does not. */}
            {movedNotice ? (
              <div className="walkin-move-notice">
                <div className="walkin-move-head">
                  <span className="walkin-move-icon" aria-hidden="true">
                    ⟳
                  </span>
                  <div>
                    <strong>Time changed</strong>
                    <span>
                      The slot was taken while the customer was paying, so this walk-in
                      was moved automatically.
                    </span>
                  </div>
                </div>
                <div className="walkin-move-compare">
                  <div className="walkin-move-col was">
                    <span className="walkin-move-label">Originally</span>
                    <span className="walkin-move-value">
                      {formatClockTime(movedNotice.from) || "—"}
                    </span>
                    {movedNotice.fromDate &&
                    movedNotice.fromDate !== movedNotice.toDate ? (
                      <span className="walkin-move-date">
                        {movedNotice.fromDate}
                      </span>
                    ) : null}
                  </div>
                  <span className="walkin-move-arrow" aria-hidden="true">
                    →
                  </span>
                  <div className="walkin-move-col now">
                    <span className="walkin-move-label">Now booked</span>
                    <span className="walkin-move-value">
                      {formatClockTime(movedNotice.to) || "—"}
                    </span>
                    {movedNotice.toDate &&
                    movedNotice.toDate !== movedNotice.fromDate ? (
                      <span className="walkin-move-date">{movedNotice.toDate}</span>
                    ) : null}
                    {movedNotice.toEnd ? (
                      <span className="walkin-move-date">
                        until {formatClockTime(movedNotice.toEnd)}
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="walkin-move-foot">
                  The customer&rsquo;s confirmation page shows the same new time.
                </p>
              </div>
            ) : (
              <p className="walkin-muted">
                The walk-in is confirmed and the booking has been updated
                automatically.
              </p>
            )}
            <div className="walkin-actions">
              <button
                type="button"
                className="walkin-btn"
                onClick={() => {
                  reset();
                }}
              >
                Start another walk-in
              </button>
              <button type="button" className="walkin-btn ghost" onClick={handleClose}>
                Done
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export default WalkInPaymentModal;
